import React from 'react';
import { ChevronRight, Home, Folder } from 'lucide-react';
import { TFileItem } from '@/data/types';

interface BreadcrumbNavigationProps {
  currentPath: string;
  onNavigateToPath: (path: string) => void;
  onMove: (file: TFileItem) => void;
  filesData?: TFileItem[];
}

const BreadcrumbNavigation: React.FC<BreadcrumbNavigationProps> = ({
  currentPath,
  onNavigateToPath,
  onMove,
  filesData = [],
}) => {
  // Parse the current path into segments
  const pathSegments = currentPath === '/' 
    ? [] 
    : currentPath.split('/').filter(segment => segment);

  // Build breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Root',
      path: '/',
      isRoot: true,
    },
    ...pathSegments.map((segment, index) => {
      const path = '/' + pathSegments.slice(0, index + 1).join('/');
      return {
        label: segment,
        path,
        isRoot: false,
      };
    }),
  ];

  const handleBreadcrumbClick = (path: string) => {
    onNavigateToPath(path);
  };

  const handleDragOver = (e: React.DragEvent, targetPath: string) => {
    e.preventDefault();
    if (targetPath !== currentPath) {
      e.currentTarget.classList.add('bg-blue-50', 'border-blue-200');
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-blue-50', 'border-blue-200');
  };

  const handleDrop = (e: React.DragEvent, targetPath: string) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-blue-50', 'border-blue-200');
    
    if (targetPath === currentPath) {
      return;
    }

    const fileId = e.dataTransfer.getData('text/plain');
    const file = filesData?.find((f) => f.id === fileId);
    if (file) {
      const updatedFile = { ...file, path: targetPath };
      onMove(updatedFile);
    }
  };

  return (
    <nav className="flex items-center space-x-2 px-1 py-3" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {breadcrumbItems.map((item, index) => {
          const isCurrentPath = index === breadcrumbItems.length - 1;
          const canReceiveDrop = !isCurrentPath;
          
          return (
            <li key={item.path} className="flex items-center">
              {index > 0 && (
                <ChevronRight className="mx-2 h-4 w-4 text-gray-400" />
              )}
              
              <button
                onClick={() => handleBreadcrumbClick(item.path)}
                onDragOver={canReceiveDrop ? (e) => handleDragOver(e, item.path) : undefined}
                onDragLeave={canReceiveDrop ? handleDragLeave : undefined}
                onDrop={canReceiveDrop ? (e) => handleDrop(e, item.path) : undefined}
                className={`
                  flex items-center space-x-1 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200 border border-transparent
                  ${
                    isCurrentPath
                      ? 'bg-blue-50 text-blue-700 cursor-default'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }
                  ${
                    canReceiveDrop 
                      ? 'hover:border-gray-200' 
                      : ''
                  }
                `}
                disabled={isCurrentPath}
                aria-current={isCurrentPath ? 'page' : undefined}
                title={canReceiveDrop ? `Drop files here to move to ${item.label}` : undefined}
              >
                {item.isRoot ? (
                  <Home className="h-4 w-4" />
                ) : (
                  <Folder className="h-4 w-4" />
                )}
                <span>{item.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default BreadcrumbNavigation;